import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Loader2, Sparkles, ChevronDown, ChevronUp, Globe, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface AddBuyerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const firmTypes = [
  "Independent Sponsor",
  "Private Equity",
  "Strategic Acquirer",
  "Family Office",
  "Search Fund",
  "Other",
];

const industries = [
  "Healthcare", "Technology", "Manufacturing", "Business Services", "Consumer",
  "Energy", "Industrial", "Financial Services", "SaaS & Technology",
  "Transportation & Logistics", "Construction & Infrastructure", "Food & Beverage", "Other",
];

const investmentThemeOptions = [
  "Founder/Owner Transitions",
  "Platform Add-ons / Bolt-ons",
  "Family-Owned Businesses",
  "Corporate Carve-outs",
  "Turnarounds / Special Situations",
  "Growth Equity",
  "Recapitalizations",
];

const dealSizes = ["$1M", "$5M", "$10M", "$25M", "$50M", "$100M", "$250M", "$500M+"];

const geographies = [
  "Texas", "Oklahoma", "Colorado", "Kansas", "Arkansas", "Louisiana",
  "New Mexico", "Arizona", "California", "Florida", "Georgia", "Illinois",
  "New York", "Ohio", "Pennsylvania", "Tennessee", "North Carolina",
  "Virginia", "Washington", "National",
];

export function AddBuyerDrawer({ open, onOpenChange }: AddBuyerDrawerProps) {
  const [firmName, setFirmName] = useState("");
  const [firmType, setFirmType] = useState("");
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");

  // Contact
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");

  // Criteria
  const [showCriteria, setShowCriteria] = useState(false);
  const [industryFocus, setIndustryFocus] = useState<"opportunistic" | "focused">("opportunistic");
  const [targetIndustries, setTargetIndustries] = useState<string[]>([]);
  const [investmentThemes, setInvestmentThemes] = useState<string[]>([]);
  const [minDealSize, setMinDealSize] = useState("");
  const [maxDealSize, setMaxDealSize] = useState("");
  const [investmentThesis, setInvestmentThesis] = useState("");
  const [geoFocus, setGeoFocus] = useState<"national" | "focused">("national");
  const [primaryGeographies, setPrimaryGeographies] = useState<string[]>([]);

  // Enrichment
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichedFields, setEnrichedFields] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const createBuyerMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/buyers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create buyer");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["buyers"] });
      toast.success("Buyer added to network successfully");
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    setFirmName("");
    setFirmType("");
    setWebsite("");
    setNotes("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setTitle("");
    setShowCriteria(false);
    setIndustryFocus("opportunistic");
    setTargetIndustries([]);
    setInvestmentThemes([]);
    setMinDealSize("");
    setMaxDealSize("");
    setInvestmentThesis("");
    setGeoFocus("national");
    setPrimaryGeographies([]);
    setEnrichedFields([]);
    onOpenChange(false);
  };

  const handleEnrich = async () => {
    if (!website.trim()) {
      toast.error("Enter a website URL first");
      return;
    }

    setIsEnriching(true);
    try {
      const res = await fetch("/api/enrich-website", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ url: website.trim(), firmName: firmName.trim() || undefined }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Enrichment failed");
      }

      const { extracted } = await res.json();
      const filled: string[] = [];

      // Auto-fill firm type if not already set
      if (extracted.firmType && !firmType) {
        setFirmType(extracted.firmType);
        filled.push("Firm Type");
      }

      // Auto-fill description into notes if empty
      if (extracted.description && !notes) {
        setNotes(extracted.description);
        filled.push("Description");
      }

      // Show criteria section and fill fields
      setShowCriteria(true);

      if (extracted.industryFocus) {
        setIndustryFocus(extracted.industryFocus);
        filled.push("Industry Focus");
      }
      if (extracted.targetIndustries?.length) {
        setTargetIndustries(extracted.targetIndustries);
        filled.push("Target Industries");
      }
      if (extracted.investmentThemes?.length) {
        setInvestmentThemes(extracted.investmentThemes);
        filled.push("Investment Themes");
      }
      if (extracted.minDealSize) {
        setMinDealSize(extracted.minDealSize);
        filled.push("Min Deal Size");
      }
      if (extracted.maxDealSize) {
        setMaxDealSize(extracted.maxDealSize);
        filled.push("Max Deal Size");
      }
      if (extracted.investmentThesis) {
        setInvestmentThesis(extracted.investmentThesis);
        filled.push("Investment Thesis");
      }
      if (extracted.geoFocus) {
        setGeoFocus(extracted.geoFocus);
        filled.push("Geo Focus");
      }
      if (extracted.primaryGeographies?.length) {
        setPrimaryGeographies(extracted.primaryGeographies);
        filled.push("Geographies");
      }

      setEnrichedFields(filled);
      toast.success(`Enriched ${filled.length} fields from website`);
    } catch (error: any) {
      toast.error(error.message || "Failed to enrich from website");
    } finally {
      setIsEnriching(false);
    }
  };

  const handleSave = () => {
    if (!firmName.trim()) {
      toast.error("Firm name is required");
      return;
    }
    if (!firmType) {
      toast.error("Firm type is required");
      return;
    }

    const hasContactInfo = firstName || lastName || email || phone || title;
    if (hasContactInfo && (!firstName.trim() || !lastName.trim() || !email.trim())) {
      toast.error("If adding a contact, first name, last name, and email are required");
      return;
    }

    const hasCriteria = industryFocus !== "opportunistic" || targetIndustries.length > 0 ||
      investmentThemes.length > 0 || minDealSize || maxDealSize ||
      investmentThesis || geoFocus !== "national" || primaryGeographies.length > 0;

    createBuyerMutation.mutate({
      firmName: firmName.trim(),
      firmType,
      website: website || null,
      notes: notes || null,
      contact: hasContactInfo ? {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone || null,
        title: title || null,
      } : null,
      criteria: hasCriteria ? {
        industryFocus,
        targetIndustries,
        investmentThemes,
        minDealSize: minDealSize || null,
        maxDealSize: maxDealSize || null,
        investmentThesis: investmentThesis || null,
        geoFocus,
        primaryGeographies,
      } : undefined,
    });
  };

  const toggleArrayItem = (arr: string[], item: string, setter: (v: string[]) => void) => {
    setter(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] flex flex-col p-0" data-testid="add-buyer-drawer">
        <DialogHeader className="px-6 py-5 border-b border-border flex-shrink-0 bg-gradient-to-r from-primary to-primary/80 rounded-t-lg">
          <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Add to Buyer Network
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Firm Information */}
          <div className="space-y-4">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
              Firm Information
            </Label>

            <div className="space-y-2">
              <Label htmlFor="firmName">Firm Name <span className="text-red-500">*</span></Label>
              <Input
                id="firmName"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                placeholder="e.g., Apex Capital Partners"
                className="h-11"
                data-testid="input-firm-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Firm Type <span className="text-red-500">*</span></Label>
              <Select value={firmType} onValueChange={setFirmType}>
                <SelectTrigger className={cn("h-11", enrichedFields.includes("Firm Type") && "ring-2 ring-blue-300")} data-testid="select-firm-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {firmTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="flex gap-2">
                <Input
                  id="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://..."
                  className="h-11 flex-1"
                  data-testid="input-website"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="h-11 gap-2 shrink-0"
                  onClick={handleEnrich}
                  disabled={isEnriching || !website.trim()}
                >
                  {isEnriching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {isEnriching ? "Enriching..." : "Enrich"}
                </Button>
              </div>
              {enrichedFields.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Sparkles className="w-3 h-3 text-blue-500" />
                  <span className="text-xs text-blue-600 dark:text-blue-400">Auto-filled:</span>
                  {enrichedFields.map((f) => (
                    <Badge key={f} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900">
                      {f}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Description / Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Firm overview, strategy, etc."
                className={cn("min-h-[60px]", enrichedFields.includes("Description") && "ring-2 ring-blue-300")}
                data-testid="input-notes"
              />
            </div>
          </div>

          {/* Acquisition Criteria */}
          <div className="pt-4 border-t border-border">
            <button
              type="button"
              onClick={() => setShowCriteria(!showCriteria)}
              className="flex items-center justify-between w-full text-left"
            >
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer">
                  Acquisition Criteria
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">Industry focus, deal sizes, geography</p>
              </div>
              {showCriteria ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {showCriteria && (
              <div className="space-y-4 mt-4 animate-fade-in">
                {/* Industry Focus */}
                <div className="space-y-2">
                  <Label className="text-sm">Industry Focus</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={industryFocus === "opportunistic" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setIndustryFocus("opportunistic"); setTargetIndustries([]); }}
                      className="flex-1"
                    >
                      Opportunistic
                    </Button>
                    <Button
                      type="button"
                      variant={industryFocus === "focused" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setIndustryFocus("focused")}
                      className="flex-1"
                    >
                      Focused
                    </Button>
                  </div>
                  {industryFocus === "focused" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {industries.map((ind) => (
                        <button
                          key={ind}
                          type="button"
                          onClick={() => toggleArrayItem(targetIndustries, ind, setTargetIndustries)}
                          className={cn(
                            "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                            targetIndustries.includes(ind)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border hover:border-primary hover:text-primary-text"
                          )}
                        >
                          {ind}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Investment Themes */}
                <div className="space-y-2">
                  <Label className="text-sm">Investment Themes</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {investmentThemeOptions.map((theme) => (
                      <button
                        key={theme}
                        type="button"
                        onClick={() => toggleArrayItem(investmentThemes, theme, setInvestmentThemes)}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                          investmentThemes.includes(theme)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-primary hover:text-primary-text"
                        )}
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Deal Size */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Min Deal Size</Label>
                    <Select value={minDealSize} onValueChange={setMinDealSize}>
                      <SelectTrigger className={cn("h-9", enrichedFields.includes("Min Deal Size") && "ring-2 ring-blue-300")}>
                        <SelectValue placeholder="Min..." />
                      </SelectTrigger>
                      <SelectContent>
                        {dealSizes.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Max Deal Size</Label>
                    <Select value={maxDealSize} onValueChange={setMaxDealSize}>
                      <SelectTrigger className={cn("h-9", enrichedFields.includes("Max Deal Size") && "ring-2 ring-blue-300")}>
                        <SelectValue placeholder="Max..." />
                      </SelectTrigger>
                      <SelectContent>
                        {dealSizes.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Investment Thesis */}
                <div className="space-y-1">
                  <Label className="text-sm">Investment Thesis</Label>
                  <Textarea
                    value={investmentThesis}
                    onChange={(e) => setInvestmentThesis(e.target.value)}
                    placeholder="What does this firm look for?"
                    className={cn("min-h-[50px] text-sm", enrichedFields.includes("Investment Thesis") && "ring-2 ring-blue-300")}
                  />
                </div>

                {/* Geographic Focus */}
                <div className="space-y-2">
                  <Label className="text-sm">Geographic Focus</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={geoFocus === "national" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setGeoFocus("national"); setPrimaryGeographies([]); }}
                      className="flex-1"
                    >
                      National
                    </Button>
                    <Button
                      type="button"
                      variant={geoFocus === "focused" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setGeoFocus("focused")}
                      className="flex-1"
                    >
                      Regional
                    </Button>
                  </div>
                  {geoFocus === "focused" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {geographies.map((geo) => (
                        <button
                          key={geo}
                          type="button"
                          onClick={() => toggleArrayItem(primaryGeographies, geo, setPrimaryGeographies)}
                          className={cn(
                            "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                            primaryGeographies.includes(geo)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border hover:border-primary hover:text-primary-text"
                          )}
                        >
                          {geo}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Primary Contact */}
          <div className="space-y-4 pt-4 border-t border-border">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
                Primary Contact
              </Label>
              <p className="text-xs text-muted-foreground mt-1">Optional — can be added later.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-9"
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-9"
                  data-testid="input-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-9"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Title / Role</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Managing Partner"
                className="h-9"
                data-testid="input-title"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={createBuyerMutation.isPending} data-testid="btn-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={createBuyerMutation.isPending || !firmName.trim() || !firmType}
            className="gold-gradient text-primary-foreground font-semibold btn-gold"
            data-testid="btn-save"
          >
            {createBuyerMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add to Buyer Network"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
